import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../AuthProvider";
import {
  fetchPlatformSettings,
  savePlatformSettings,
} from "../services/platformSettingsService";
import {
  createValueConfig,
  fetchValueConfigs,
} from "../services/valueConfigService";
import {
  fetchPushRecipients,
  sendTestPushNotification,
} from "../services/notificationService";

const formatTimestamp = (value) => {
  if (!value) return "—";
  try {
    return new Intl.DateTimeFormat("de-CH", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));
  } catch {
    return value;
  }
};

const cloneRows = (items) => items.map((item) => ({ ...item }));

const formatCurrency = (value) => {
  if (value == null) return "—";
  try {
    return new Intl.NumberFormat("de-CH", {
      style: "currency",
      currency: "CHF",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(value));
  } catch {
    return value;
  }
};

const formatDecimal = (value) => {
  if (value == null) return "—";
  try {
    return new Intl.NumberFormat("de-CH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(value));
  } catch {
    return value;
  }
};

const EMPTY_VALUE_CONFIG = {
  cpk: "",
  car_price: "",
  view_per_km_estimate: "",
};
const DEFAULT_NOTIFICATION_FORM = {
  target: "user",
  userId: "",
  title: "WerbeAuto Test",
  body: "Dies ist eine Testbenachrichtigung.",
};
const DRIVE_PHOTO_CHANCE_KEY = "drive_verification_photo_chance_percentage";
const CAMPAIGN_MIN_DURATION_KEY = "campaign_min_duration_days";

export default function PlatformSettingsPage() {
  const { fetchWithAuth } = useAuth();
  const [rows, setRows] = useState([]);
  const [baseline, setBaseline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [configs, setConfigs] = useState([]);
  const [configsLoading, setConfigsLoading] = useState(true);
  const [configError, setConfigError] = useState(null);
  const [configSuccess, setConfigSuccess] = useState(false);
  const [creatingConfig, setCreatingConfig] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_VALUE_CONFIG);
  const [createTouched, setCreateTouched] = useState({});
  const [pushRecipients, setPushRecipients] = useState([]);
  const [pushLoading, setPushLoading] = useState(true);
  const [pushSending, setPushSending] = useState(false);
  const [pushError, setPushError] = useState(null);
  const [pushSuccess, setPushSuccess] = useState(null);
  const [pushForm, setPushForm] = useState(DEFAULT_NOTIFICATION_FORM);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const payload = await fetchPlatformSettings(fetchWithAuth);
      const normalized = (payload ?? [])
        .map((item) => ({
          key: item.key ?? "",
          value: item.value ?? "",
          updatedAt: item.updated_at ?? null,
        }))
        .sort((a, b) => a.key.localeCompare(b.key));
      if (!normalized.some((item) => item.key === CAMPAIGN_MIN_DURATION_KEY)) {
        normalized.push({ key: CAMPAIGN_MIN_DURATION_KEY, value: "28", updatedAt: null });
      }
      setRows(cloneRows(normalized));
      setBaseline(cloneRows(normalized));
    } catch (err) {
      setError(err.message || "Einstellungen konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const loadConfigs = useCallback(async () => {
    setConfigsLoading(true);
    setConfigError(null);
    setConfigSuccess(false);
    try {
      const payload = await fetchValueConfigs(fetchWithAuth);
      const normalized = (payload ?? []).map((item) => ({
        id: item.id,
        cpk: item.cpk,
        car_price: item.car_price,
        view_per_km_estimate: item.view_per_km_estimate,
        inserted_at: item.inserted_at,
      }));
      setConfigs(normalized);
    } catch (err) {
      setConfigError(
        err.message || "Value-Konfigurationen konnten nicht geladen werden."
      );
    } finally {
      setConfigsLoading(false);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  const loadPushRecipients = useCallback(async () => {
    setPushLoading(true);
    setPushError(null);
    try {
      const payload = await fetchPushRecipients(fetchWithAuth);
      const normalized = (payload ?? []).map((item) => ({
        userId: item.user_id,
        displayName: item.display_name || item.user_id,
        city: item.address_city || "",
        tokenCount: Number(item.active_token_count || 0),
        lastRegisteredAt: item.last_registered_at || null,
      }));
      setPushRecipients(normalized);
      setPushForm((prev) => {
        if (prev.userId || normalized.length === 0) {
          return prev;
        }
        return { ...prev, userId: normalized[0].userId };
      });
    } catch (err) {
      setPushError(err.message || "Empfänger konnten nicht geladen werden.");
    } finally {
      setPushLoading(false);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    loadPushRecipients();
  }, [loadPushRecipients]);

  useEffect(() => {
    if (!success) return undefined;
    const timer = window.setTimeout(() => setSuccess(false), 4000);
    return () => window.clearTimeout(timer);
  }, [success]);

  useEffect(() => {
    if (!configSuccess) return undefined;
    const timer = window.setTimeout(() => setConfigSuccess(false), 4000);
    return () => window.clearTimeout(timer);
  }, [configSuccess]);

  useEffect(() => {
    if (!pushSuccess) return undefined;
    const timer = window.setTimeout(() => setPushSuccess(null), 5000);
    return () => window.clearTimeout(timer);
  }, [pushSuccess]);

  const comparableBaseline = useMemo(
    () => baseline.map(({ key, value }) => ({ key, value })),
    [baseline]
  );
  const comparableCurrent = useMemo(
    () => rows.map(({ key, value }) => ({ key, value })),
    [rows]
  );
  const isDirty = useMemo(() => {
    return JSON.stringify(comparableBaseline) !== JSON.stringify(comparableCurrent);
  }, [comparableBaseline, comparableCurrent]);

  const rowErrors = useMemo(() => {
    const keyCounts = rows.reduce((acc, row) => {
      const trimmed = (row.key || "").trim();
      if (!trimmed) return acc;
      acc[trimmed] = (acc[trimmed] || 0) + 1;
      return acc;
    }, {});
    return rows.map((row) => {
      const trimmed = (row.key || "").trim();
      const errors = {};
      if (!trimmed) {
        errors.key = "Schlüssel erforderlich";
      } else if (keyCounts[trimmed] > 1) {
        errors.key = "Schlüssel muss eindeutig sein";
      }
      if (trimmed === DRIVE_PHOTO_CHANCE_KEY) {
        const rawValue = String(row.value ?? "").trim();
        if (!/^\d+$/.test(rawValue)) {
          errors.value = "Wert muss eine ganze Zahl zwischen 0 und 100 sein";
        } else {
          const parsed = Number(rawValue);
          if (parsed < 0 || parsed > 100) {
            errors.value = "Wert muss zwischen 0 und 100 liegen";
          }
        }
      } else if (trimmed === CAMPAIGN_MIN_DURATION_KEY) {
        const rawValue = String(row.value ?? "").trim();
        if (!/^\d+$/.test(rawValue) || Number(rawValue) <= 0) {
          errors.value = "Wert muss eine positive ganze Zahl an Tagen sein";
        }
      }
      return errors;
    });
  }, [rows]);

  const hasErrors = useMemo(
    () => rowErrors.some((entry) => Object.keys(entry).length > 0),
    [rowErrors]
  );

  const handleFieldChange = (index, field, value) => {
    setRows((prev) =>
      prev.map((row, idx) => (idx === index ? { ...row, [field]: value } : row))
    );
    setSuccess(false);
  };

  const handleAddRow = () => {
    setRows((prev) => [...prev, { key: "", value: "", updatedAt: null }]);
    setSuccess(false);
  };

  const handleReset = () => {
    setRows(cloneRows(baseline));
    setError(null);
    setSuccess(false);
  };

  const handleSave = async () => {
    if (hasErrors) {
      setError("Bitte behebe die markierten Fehler, bevor du speicherst.");
      return;
    }
    const payload = rows.map((row) => ({
      key: (row.key || "").trim(),
      value: row.value ?? "",
    }));
    setSaving(true);
    setError(null);
    try {
      const saved = await savePlatformSettings(fetchWithAuth, payload);
      const normalized = (saved ?? []).map((item) => ({
        key: item.key ?? "",
        value: item.value ?? "",
        updatedAt: item.updated_at ?? null,
      }));
      setRows(cloneRows(normalized));
      setBaseline(cloneRows(normalized));
      setSuccess(true);
    } catch (err) {
      setError(err.message || "Einstellungen konnten nicht gespeichert werden.");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateFieldChange = (field, value) => {
    setCreateForm((prev) => ({ ...prev, [field]: value }));
    setConfigError(null);
    setConfigSuccess(false);
  };

  const handleCreateFieldBlur = (field) => {
    setCreateTouched((prev) => ({ ...prev, [field]: true }));
  };

  const createErrors = useMemo(() => {
    const errors = {};
    ["cpk", "car_price", "view_per_km_estimate"].forEach((field) => {
      const raw = createForm[field];
      if (raw === "" || raw === null) {
        errors[field] = "Pflichtfeld";
        return;
      }
      const parsed = Number(raw);
      if (Number.isNaN(parsed)) {
        errors[field] = "Ungültige Zahl";
      } else if (parsed <= 0) {
        errors[field] = "Muss größer als 0 sein";
      }
    });
    return errors;
  }, [createForm]);

  const hasCreateErrors = useMemo(
    () => Object.keys(createErrors).length > 0,
    [createErrors]
  );

  const getCreateError = (field) =>
    createTouched[field] && createErrors[field] ? createErrors[field] : null;

  const handleCreateConfig = async (event) => {
    event.preventDefault();
    setCreateTouched({
      cpk: true,
      car_price: true,
      view_per_km_estimate: true,
    });
    if (hasCreateErrors) {
      setConfigError("Bitte gültige Werte für alle Felder eingeben.");
      return;
    }
    setCreatingConfig(true);
    setConfigError(null);
    try {
      await createValueConfig(fetchWithAuth, {
        cpk: Number(createForm.cpk),
        car_price: Number(createForm.car_price),
        view_per_km_estimate: Number(createForm.view_per_km_estimate),
      });
      await loadConfigs();
      setCreateForm(EMPTY_VALUE_CONFIG);
      setCreateTouched({});
      setConfigSuccess(true);
    } catch (err) {
      setConfigError(
        err.message || "Value-Konfiguration konnte nicht erstellt werden."
      );
    } finally {
      setCreatingConfig(false);
    }
  };

  const selectedRecipient = useMemo(
    () => pushRecipients.find((recipient) => recipient.userId === pushForm.userId) || null,
    [pushForm.userId, pushRecipients]
  );

  const totalPushTokens = useMemo(
    () => pushRecipients.reduce((sum, recipient) => sum + recipient.tokenCount, 0),
    [pushRecipients]
  );

  const handlePushFieldChange = (field, value) => {
    setPushForm((prev) => ({ ...prev, [field]: value }));
    setPushError(null);
    setPushSuccess(null);
  };

  const handleSendTestPush = async (event) => {
    event.preventDefault();
    const title = pushForm.title.trim();
    const body = pushForm.body.trim();
    if (!title || !body) {
      setPushError("Titel und Nachricht sind erforderlich.");
      return;
    }
    if (pushForm.target === "user" && !pushForm.userId) {
      setPushError("Bitte wähle einen Empfänger aus.");
      return;
    }

    setPushSending(true);
    setPushError(null);
    setPushSuccess(null);
    try {
      const result = await sendTestPushNotification(fetchWithAuth, {
        target: pushForm.target,
        user_id: pushForm.target === "user" ? pushForm.userId : null,
        title,
        body,
      });
      const count = result?.recipient_count ?? 0;
      setPushSuccess(`Testbenachrichtigung an ${count} aktive Geräte gesendet.`);
    } catch (err) {
      setPushError(
        err.message || "Testbenachrichtigung konnte nicht gesendet werden."
      );
    } finally {
      setPushSending(false);
    }
  };

  return (
    <>
      <div className="portal-section settings-panel">
      <div className="settings-top">
        <div>
          <p className="settings-eyebrow">Platform</p>
          <h1 className="portal-title">Platform Einstellungen</h1>
          <p className="portal-desc">
            Verwalte globale Parameter wie Zahlungsdaten oder Feature-Flags. Änderungen
            werden sofort aktiv.
          </p>
        </div>
        <button
          className="settings-refresh"
          onClick={loadSettings}
          disabled={loading || saving}
        >
          Neu laden
        </button>
      </div>

      {error && <div className="car-alert error">{error}</div>}
      {success && <div className="car-alert success">Einstellungen gespeichert.</div>}

      {loading ? (
        <div className="car-empty">Einstellungen werden geladen…</div>
      ) : (
        <>
          <div className="settings-grid">
            {rows.length === 0 ? (
              <div className="car-empty">Noch keine Einstellungen vorhanden.</div>
            ) : (
              rows.map((row, idx) => {
                const keyError = rowErrors[idx]?.key;
                const valueError = rowErrors[idx]?.value;
                const rowKey = `${row.key || "setting"}-${idx}`;
                const isDrivePhotoChance = (row.key || "").trim() === DRIVE_PHOTO_CHANCE_KEY;
                const isCampaignMinDuration = (row.key || "").trim() === CAMPAIGN_MIN_DURATION_KEY;
                return (
                  <div key={rowKey} className="settings-row">
                    <div className="settings-row-fields">
                      <label className="settings-field">
                        <span>Schlüssel</span>
                        <input
                          type="text"
                          value={row.key}
                          onChange={(event) =>
                            handleFieldChange(idx, "key", event.target.value)
                          }
                          className={`settings-input${keyError ? " has-error" : ""}`}
                          placeholder="z.B. advertiser_payment_iban"
                        />
                      </label>
                      <label className="settings-field">
                        <span>Wert</span>
                        {isDrivePhotoChance || isCampaignMinDuration ? (
                            <input
                              type="number"
                              min="1"
                              step="1"
                            value={row.value}
                            onChange={(event) =>
                              handleFieldChange(idx, "value", event.target.value)
                            }
                            className={`settings-input${valueError ? " has-error" : ""}`}
                            placeholder={isDrivePhotoChance ? "0 bis 100" : "Anzahl Tage"}
                          />
                        ) : (
                          <textarea
                            rows={2}
                            value={row.value}
                            onChange={(event) =>
                              handleFieldChange(idx, "value", event.target.value)
                            }
                            className={`settings-input${valueError ? " has-error" : ""}`}
                            placeholder="Neuer Wert"
                          />
                        )}
                      </label>
                    </div>
                    <div className="settings-row-footer">
                      <span className="settings-updated">
                        Zuletzt aktualisiert: {formatTimestamp(row.updatedAt)}
                      </span>
                    </div>
                    {keyError && (
                      <div className="settings-field-error">{keyError}</div>
                    )}
                    {valueError && (
                      <div className="settings-field-error">{valueError}</div>
                    )}
                  </div>
                );
              })
            )}
          </div>
          <div className="settings-actions">
            <button className="settings-plus" onClick={handleAddRow} disabled={saving}>
              Neue Einstellung
            </button>
            <div className="settings-actions-right">
              <button
                className="settings-reset"
                onClick={handleReset}
                disabled={!isDirty || saving}
              >
                Änderungen verwerfen
              </button>
              <button
                className="settings-save"
                onClick={handleSave}
                disabled={!isDirty || hasErrors || saving}
              >
                Speichern
              </button>
            </div>
          </div>
        </>
      )}
    </div>

      <div className="portal-section notification-test-panel">
        <div className="settings-top">
          <div>
            <p className="settings-eyebrow">Notifications</p>
            <h2 className="value-config-title">Push-Benachrichtigungen testen</h2>
            <p className="value-config-desc">
              Sende eine Testnachricht an einen einzelnen Driver oder an alle registrierten Driver-Geräte.
            </p>
          </div>
          <button
            className="settings-refresh"
            onClick={loadPushRecipients}
            disabled={pushLoading || pushSending}
          >
            Empfänger neu laden
          </button>
        </div>

        {pushError && <div className="car-alert error">{pushError}</div>}
        {pushSuccess && <div className="car-alert success">{pushSuccess}</div>}

        {pushLoading ? (
          <div className="car-empty">Push-Empfänger werden geladen…</div>
        ) : (
          <form className="notification-test-form" onSubmit={handleSendTestPush}>
            <div className="notification-target-grid">
              <label className="notification-target-card">
                <input
                  type="radio"
                  name="push-target"
                  value="user"
                  checked={pushForm.target === "user"}
                  onChange={() => handlePushFieldChange("target", "user")}
                />
                <span>
                  <strong>Ausgewählter Driver</strong>
                  <small>
                    {selectedRecipient
                      ? `${selectedRecipient.displayName} · ${selectedRecipient.tokenCount} Gerät(e)`
                      : "Ein aktives Gerät erforderlich"}
                  </small>
                </span>
              </label>
              <label className="notification-target-card">
                <input
                  type="radio"
                  name="push-target"
                  value="global"
                  checked={pushForm.target === "global"}
                  onChange={() => handlePushFieldChange("target", "global")}
                />
                <span>
                  <strong>Global</strong>
                  <small>{totalPushTokens} aktive Geräte über alle Driver</small>
                </span>
              </label>
            </div>

            <div className="settings-row-fields">
              <label className="settings-field">
                <span>Empfänger</span>
                <select
                  className="settings-input"
                  value={pushForm.userId}
                  onChange={(event) =>
                    handlePushFieldChange("userId", event.target.value)
                  }
                  disabled={pushForm.target !== "user" || pushSending}
                >
                  {pushRecipients.length === 0 ? (
                    <option value="">Keine aktiven Geräte vorhanden</option>
                  ) : (
                    pushRecipients.map((recipient) => (
                      <option key={recipient.userId} value={recipient.userId}>
                        {recipient.displayName}
                        {recipient.city ? ` · ${recipient.city}` : ""}
                        {` · ${recipient.tokenCount} Gerät(e)`}
                      </option>
                    ))
                  )}
                </select>
              </label>
              <label className="settings-field">
                <span>Titel</span>
                <input
                  className="settings-input"
                  type="text"
                  value={pushForm.title}
                  onChange={(event) =>
                    handlePushFieldChange("title", event.target.value)
                  }
                  disabled={pushSending}
                />
              </label>
            </div>

            <label className="settings-field">
              <span>Nachricht</span>
              <textarea
                className="settings-input"
                rows={3}
                value={pushForm.body}
                onChange={(event) =>
                  handlePushFieldChange("body", event.target.value)
                }
                disabled={pushSending}
              />
            </label>

            <div className="settings-actions">
              <span className="settings-updated">
                Empfänger mit aktiven Push-Tokens: {pushRecipients.length}
              </span>
              <button
                type="submit"
                className="settings-save"
                disabled={
                  pushSending ||
                  (pushForm.target === "user" && !pushForm.userId) ||
                  totalPushTokens === 0
                }
              >
                Testbenachrichtigung senden
              </button>
            </div>
          </form>
        )}
      </div>

      <div className="portal-section value-config-panel">
        <div className="value-config-top">
          <div>
            <p className="settings-eyebrow">Value Calculation</p>
            <h2 className="value-config-title">Value-Konfigurationen</h2>
            <p className="value-config-desc">
              Lege neue Werte für CPK, Fahrzeugkosten und View-per-km fest. Bestehende
              Einträge bleiben zur Nachverfolgung erhalten.
            </p>
          </div>
          <button
            className="settings-refresh"
            onClick={loadConfigs}
            disabled={configsLoading || creatingConfig}
          >
            Neu laden
          </button>
        </div>

        {configError && <div className="car-alert error">{configError}</div>}
        {configSuccess && (
          <div className="car-alert success">
            Neue Value-Konfiguration gespeichert.
          </div>
        )}

        {configsLoading ? (
          <div className="car-empty">Konfigurationen werden geladen…</div>
        ) : (
          <>
            <div className="value-config-grid">
              {configs.length === 0 ? (
                <div className="car-empty">Noch keine Konfigurationen vorhanden.</div>
              ) : (
                configs.map((config, idx) => (
                  <div
                    key={config.id}
                    className={`value-config-card${idx === 0 ? " highlight" : ""}`}
                  >
                    <div className="value-config-chip">
                      {idx === 0 ? "Aktuell" : "Historisch"}
                    </div>
                    <div className="value-config-rows">
                      <div className="value-config-row">
                        <span>CPK</span>
                        <strong>{formatCurrency(config.cpk)}</strong>
                      </div>
                      <div className="value-config-row">
                        <span>Fahrzeugpreis</span>
                        <strong>{formatCurrency(config.car_price)}</strong>
                      </div>
                      <div className="value-config-row">
                        <span>View pro km</span>
                        <strong>{formatDecimal(config.view_per_km_estimate)}</strong>
                      </div>
                    </div>
                    <div className="value-config-updated">
                      Gültig seit {formatTimestamp(config.inserted_at)}
                    </div>
                  </div>
                ))
              )}
            </div>

            <form className="value-config-form" onSubmit={handleCreateConfig}>
              <h3>Neue Konfiguration erfassen</h3>
              <div className="value-config-form-grid">
                <label className="settings-field">
                  <span>CPK (CHF)</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={createForm.cpk}
                    onChange={(event) =>
                      handleCreateFieldChange("cpk", event.target.value)
                    }
                    onBlur={() => handleCreateFieldBlur("cpk")}
                    className={`settings-input${
                      getCreateError("cpk") ? " has-error" : ""
                    }`}
                    placeholder="z.B. 1.25"
                  />
                  {getCreateError("cpk") && (
                    <span className="settings-field-error">
                      {getCreateError("cpk")}
                    </span>
                  )}
                </label>
                <label className="settings-field">
                  <span>Fahrzeugpreis (CHF)</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={createForm.car_price}
                    onChange={(event) =>
                      handleCreateFieldChange("car_price", event.target.value)
                    }
                    onBlur={() => handleCreateFieldBlur("car_price")}
                    className={`settings-input${
                      getCreateError("car_price") ? " has-error" : ""
                    }`}
                    placeholder="z.B. 25000"
                  />
                  {getCreateError("car_price") && (
                    <span className="settings-field-error">
                      {getCreateError("car_price")}
                    </span>
                  )}
                </label>
                <label className="settings-field">
                  <span>View pro km</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={createForm.view_per_km_estimate}
                    onChange={(event) =>
                      handleCreateFieldChange(
                        "view_per_km_estimate",
                        event.target.value
                      )
                    }
                    onBlur={() => handleCreateFieldBlur("view_per_km_estimate")}
                    className={`settings-input${
                      getCreateError("view_per_km_estimate") ? " has-error" : ""
                    }`}
                    placeholder="z.B. 0.35"
                  />
                  {getCreateError("view_per_km_estimate") && (
                    <span className="settings-field-error">
                      {getCreateError("view_per_km_estimate")}
                    </span>
                  )}
                </label>
              </div>
              <div className="value-config-form-actions">
                <button
                  type="submit"
                  className="value-config-submit"
                  disabled={creatingConfig}
                >
                  Neue Konfiguration speichern
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </>
  );
}
